(function (root, factory) {
  'use strict';

  var moduleInterface = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = moduleInterface;
    return;
  }
  if (root && root.document) moduleInterface.install(root);
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var SUPPORTED_VERSION = 1;
  var HOST_SELECTOR = '[data-sa-comparison-host]';
  var TRIGGER_SELECTOR = 'a[data-sa-comparison-open]';
  var HASH_INTENTS = {
    '#compare': 'listen',
    '#compare-sound': 'listen',
    '#compare-listen': 'listen',
    '#compare-specs': 'specs'
  };

  function fail(message) {
    throw new Error('Invalid Product Comparison catalogue: ' + message);
  }

  function nonEmpty(value, name) {
    if (typeof value !== 'string' || !value.trim()) fail(name + ' must be a non-empty string.');
  }

  function uniqueIds(items, key, name) {
    if (!Array.isArray(items) || !items.length) fail(name + ' must be a non-empty array.');
    var seen = new Set();
    items.forEach(function (item) {
      if (!item || typeof item !== 'object') fail(name + ' entries must be objects.');
      nonEmpty(item[key], name + ' ' + key);
      if (seen.has(item[key])) fail(name + ' ' + key + ' values must be unique.');
      seen.add(item[key]);
    });
    return seen;
  }

  function validateHttps(value, name) {
    nonEmpty(value, name);
    var parsed;
    try { parsed = new URL(value); } catch (error) { fail(name + ' must be a valid URL.'); }
    if (parsed.protocol !== 'https:') fail(name + ' must use HTTPS.');
  }

  function validateCatalogue(catalogue) {
    if (!catalogue || typeof catalogue !== 'object' || Array.isArray(catalogue)) fail('the root must be an object.');
    if (catalogue.schemaVersion !== SUPPORTED_VERSION) fail('unsupported schema version.');

    nonEmpty(catalogue.mediaUrlPattern, 'mediaUrlPattern');
    var tokens = catalogue.mediaUrlPattern.match(/\{[^}]+\}/g) || [];
    if (tokens.length !== 3 || tokens.filter(function (token) { return token === '{song}'; }).length !== 1 ||
        tokens.filter(function (token) { return token === '{mediaId}'; }).length !== 1 ||
        tokens.filter(function (token) { return token === '{mix}'; }).length !== 1) {
      fail('mediaUrlPattern must contain {song}, {mediaId}, and {mix} exactly once.');
    }
    validateHttps(catalogue.mediaUrlPattern
      .replace('{song}', 'song')
      .replace('{mediaId}', 'media')
      .replace('{mix}', 'mix'), 'mediaUrlPattern');

    var songIds = uniqueIds(catalogue.songs, 'id', 'songs');
    var mixIds = uniqueIds(catalogue.mixes, 'id', 'mixes');
    catalogue.songs.forEach(function (song) { nonEmpty(song.label, 'song label'); });
    catalogue.mixes.forEach(function (mix) { nonEmpty(mix.label, 'mix label'); });

    var engineIds = uniqueIds(catalogue.specificationEngines, 'id', 'specificationEngines');
    var articulationIds = new Map();
    catalogue.specificationEngines.forEach(function (engine) {
      nonEmpty(engine.label, 'specification engine label');
      var ids = uniqueIds(engine.articulations, 'id', engine.id + ' articulations');
      engine.articulations.forEach(function (articulation) { nonEmpty(articulation.label, 'articulation label'); });
      articulationIds.set(engine.id, ids);
    });

    var mediaIds = uniqueIds(catalogue.products, 'mediaId', 'products');
    var productHandles = uniqueIds(catalogue.products, 'productHandle', 'products');
    catalogue.products.forEach(function (product) {
      nonEmpty(product.label, 'Product label');
      if (typeof product.legacy !== 'boolean') fail(product.mediaId + ' legacy must be boolean.');
      validateHttps(product.image, product.mediaId + ' image');
      if (!product.summary || typeof product.summary !== 'object') fail(product.mediaId + ' summary is required.');
      nonEmpty(product.summary.playstyle, product.mediaId + ' playstyle');
      nonEmpty(product.summary.context, product.mediaId + ' context');
      if (!product.specs || typeof product.specs !== 'object') fail(product.mediaId + ' specs are required.');
      if (!engineIds.has(product.specs.engine)) fail(product.mediaId + ' has an unknown specification engine.');
      if (!Number.isInteger(product.specs.tones) || product.specs.tones < 0) fail(product.mediaId + ' tones must be a non-negative integer.');
      if (!Number.isInteger(product.specs.strings) || product.specs.strings < 1) fail(product.mediaId + ' strings must be a positive integer.');
      ['lowestNote', 'highestNote'].forEach(function (key) {
        if (product.specs[key] !== null) nonEmpty(product.specs[key], product.mediaId + ' ' + key);
      });
      if (!Array.isArray(product.specs.articulations)) fail(product.mediaId + ' articulations must be an array.');
      var known = articulationIds.get(product.specs.engine);
      var productArticulations = new Set();
      product.specs.articulations.forEach(function (articulation) {
        if (!known.has(articulation)) fail(product.mediaId + ' has an unknown articulation.');
        if (productArticulations.has(articulation)) fail(product.mediaId + ' has a duplicate articulation.');
        productArticulations.add(articulation);
      });
    });

    if (!Array.isArray(catalogue.unavailable)) fail('unavailable must be an array.');
    var unavailable = new Set();
    catalogue.unavailable.forEach(function (selection) {
      if (!selection || !songIds.has(selection.song) || !mixIds.has(selection.mix) || !mediaIds.has(selection.mediaId)) {
        fail('unavailable entries must reference known song, mix, and media IDs.');
      }
      var key = selection.song + '|' + selection.mediaId + '|' + selection.mix;
      if (unavailable.has(key)) fail('unavailable entries must be unique.');
      unavailable.add(key);
    });
    if (songIds.size * mixIds.size * productHandles.size <= unavailable.size) fail('at least one playable combination is required.');
    return catalogue;
  }

  function buildMediaUrl(catalogue, song, mediaId, mix) {
    var url = catalogue.mediaUrlPattern
      .replace('{song}', encodeURIComponent(song))
      .replace('{mediaId}', encodeURIComponent(mediaId))
      .replace('{mix}', encodeURIComponent(mix));
    if (/\{[^}]+\}/.test(url)) throw new Error('Comparison media URL contains an unresolved token.');
    return url;
  }

  function createPlaybackAdapter(root, catalogue, id) {
    if (!root.SAMediaPlayback || typeof root.SAMediaPlayback.createSession !== 'function') {
      throw new Error('Media Playback is unavailable.');
    }
    var unavailable = new Set(catalogue.unavailable.map(function (selection) {
      return selection.song + '|' + selection.mediaId + '|' + selection.mix;
    }));
    var clips = [];
    var clipBySelection = new Map();
    var selectionByClip = new Map();
    var loadState = {};
    var listeners = new Set();
    var destroyed = false;

    function selectionKey(selection) {
      return selection.song + '|' + selection.mediaId + '|' + selection.mix;
    }

    function displayKey(selection) {
      return selection.song + '-' + selection.mediaId + '-' + selection.mix;
    }

    catalogue.songs.forEach(function (song, songIndex) {
      catalogue.products.forEach(function (product, productIndex) {
        catalogue.mixes.forEach(function (mix, mixIndex) {
          var selection = { song: song.id, mediaId: product.mediaId, mix: mix.id };
          var key = selectionKey(selection);
          var display = displayKey(selection);
          if (unavailable.has(key)) {
            loadState[display] = 'error';
            return;
          }
          var clip = {
            id: 's' + songIndex + '-p' + productIndex + '-m' + mixIndex,
            url: buildMediaUrl(catalogue, song.id, product.mediaId, mix.id),
            timelineId: song.id
          };
          clips.push(clip);
          clipBySelection.set(key, clip);
          selectionByClip.set(clip.id, Object.assign({ clipId: clip.id, loadKey: display }, selection));
          loadState[display] = 'idle';
        });
      });
    });
    if (!clips.length) throw new Error('No comparison audio is available.');

    var current = selectionByClip.get(clips[0].id);
    var session = root.SAMediaPlayback.createSession({ id: id, clips: clips, initialClipId: clips[0].id });
    var disposeSession = session.subscribe(function (state) {
      var selected = selectionByClip.get(state.desired.clipId);
      if (selected) {
        if (state.status === 'loading' || state.status === 'preparing') loadState[selected.loadKey] = 'loading';
        else if (state.status === 'ready' || state.status === 'playing' || state.status === 'paused') loadState[selected.loadKey] = 'ready';
        else if (state.status === 'error') loadState[selected.loadKey] = 'error';
      }
      emit();
    });

    function assertAlive() {
      if (destroyed) throw new Error('Comparison playback has been destroyed.');
    }

    function currentClip() { return clipBySelection.get(selectionKey(current)); }

    function state() {
      var mediaState = session.state;
      var selected = currentClip();
      var audibleSelected = selected && mediaState.audible.clipId === selected.id;
      return {
        song: current.song,
        mix: current.mix,
        mediaId: current.mediaId,
        playing: Boolean(audibleSelected && mediaState.audible.playing),
        position: audibleSelected ? mediaState.audible.positionSeconds : mediaState.desired.positionSeconds,
        duration: audibleSelected ? mediaState.audible.durationSeconds : 0,
        loadState: Object.assign({}, loadState),
        status: mediaState.status,
        error: mediaState.error
      };
    }

    function emit() {
      if (destroyed) return;
      var snapshot = state();
      listeners.forEach(function (listener) {
        try { listener(snapshot); } catch (error) { report(root, error, 'playback listener'); }
      });
    }

    function prepare(ids, priority) {
      if (!ids.length) return;
      ids.forEach(function (clipId) {
        var selection = selectionByClip.get(clipId);
        if (selection && loadState[selection.loadKey] !== 'ready') loadState[selection.loadKey] = 'loading';
      });
      emit();
      session.prepare(ids, { priority: priority }).then(function (outcome) {
        if (destroyed) return;
        outcome.ready.forEach(function (clipId) {
          var selection = selectionByClip.get(clipId);
          if (selection) loadState[selection.loadKey] = 'ready';
        });
        outcome.failed.forEach(function (failure) {
          var selection = selectionByClip.get(failure.clipId);
          if (selection) loadState[selection.loadKey] = 'error';
        });
        emit();
      });
    }

    function prepareCascade() {
      var selected = currentClip();
      var near = [];
      var background = [];
      catalogue.products.forEach(function (product) {
        var clip = clipBySelection.get(selectionKey({ song: current.song, mediaId: product.mediaId, mix: current.mix }));
        if (clip && clip.id !== selected.id) near.push(clip.id);
      });
      catalogue.mixes.forEach(function (mix) {
        if (mix.id === current.mix) return;
        catalogue.products.forEach(function (product) {
          var clip = clipBySelection.get(selectionKey({ song: current.song, mediaId: product.mediaId, mix: mix.id }));
          if (clip) background.push(clip.id);
        });
      });
      prepare([selected.id], 'user');
      prepare(near, 'near');
      prepare(background, 'background');
    }

    function select(next) {
      assertAlive();
      var clip = clipBySelection.get(selectionKey(next));
      if (!clip && unavailable.has(selectionKey(next))) {
        for (var index = 0; index < catalogue.products.length; index += 1) {
          var fallback = { song: next.song, mediaId: catalogue.products[index].mediaId, mix: next.mix };
          clip = clipBySelection.get(selectionKey(fallback));
          if (clip) { next = fallback; break; }
        }
      }
      if (!clip) throw new Error('Unknown comparison selection.');
      current = selectionByClip.get(clip.id);
      if (loadState[current.loadKey] === 'error') loadState[current.loadKey] = 'idle';
      var result = session.state.desired.clipId === clip.id ? Promise.resolve(session.state) : session.set({ clipId: clip.id });
      prepareCascade();
      emit();
      return result;
    }

    return Object.freeze({
      init: function (song, mix, mediaId) { return select({ song: song, mix: mix, mediaId: mediaId }); },
      setSong: function (song) { return select({ song: song, mix: current.mix, mediaId: current.mediaId }); },
      setMix: function (mix) { return select({ song: current.song, mix: mix, mediaId: current.mediaId }); },
      setMediaId: function (mediaId) { return select({ song: current.song, mix: current.mix, mediaId: mediaId }); },
      toggle: function () {
        assertAlive();
        return session.set({ playing: session.state.status === 'error' || !session.state.desired.playing });
      },
      pause: function () {
        if (destroyed || !session.state.desired.playing) return Promise.resolve(session.state);
        return session.set({ playing: false });
      },
      getState: state,
      subscribe: function (listener) {
        assertAlive();
        listeners.add(listener);
        listener(state());
        return function () { listeners.delete(listener); };
      },
      destroy: function () {
        if (destroyed) return;
        destroyed = true;
        disposeSession();
        listeners.clear();
        session.destroy();
      }
    });
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function report(root, error, phase) {
    if (root.console && typeof root.console.error === 'function') {
      root.console.error('[Product Comparison] ' + phase, error);
    }
  }

  function install(root) {
    var document = root.document;
    var sentinel = '__saComparisonInstalledV1';
    if (document[sentinel]) return;
    try {
      Object.defineProperty(document, sentinel, { value: true, configurable: false });
    } catch (error) {
      document[sentinel] = true;
    }

    var controllers = new WeakMap();
    var liveControllers = new Set();
    var cataloguePromises = new Map();

    function loadCatalogue(url, retry) {
      if (retry) cataloguePromises.delete(url);
      if (!cataloguePromises.has(url)) {
        cataloguePromises.set(url, root.fetch(url, { credentials: 'same-origin' })
          .then(function (response) {
            if (!response.ok) throw new Error('Catalogue request failed with status ' + response.status + '.');
            return response.json();
          })
          .then(validateCatalogue)
          .catch(function (error) {
            cataloguePromises.delete(url);
            throw error;
          }));
      }
      return cataloguePromises.get(url);
    }

    function queryWithin(scope, selector) {
      var found = [];
      if (scope && typeof scope.matches === 'function' && scope.matches(selector)) found.push(scope);
      if (scope && typeof scope.querySelectorAll === 'function') {
        Array.prototype.push.apply(found, scope.querySelectorAll(selector));
      }
      return found;
    }

    function mount(scope) {
      queryWithin(scope || document, HOST_SELECTOR).forEach(function (host) {
        if (controllers.has(host)) return;
        var controller = createController(root, host, loadCatalogue);
        if (!controller) return;
        controllers.set(host, controller);
        liveControllers.add(controller);
      });
    }

    function soleLiveController() {
      var hosts = Array.prototype.filter.call(document.querySelectorAll(HOST_SELECTOR), function (host) {
        return host.isConnected;
      });
      if (hosts.length !== 1) return null;
      var controller = controllers.get(hosts[0]);
      return controller && controller.live ? controller : null;
    }

    function canonicalTrigger(anchor) {
      if (!anchor || !anchor.getAttribute) return null;
      var intent = anchor.getAttribute('data-sa-comparison-open');
      if (intent !== 'listen' && intent !== 'specs') return null;
      var href = anchor.getAttribute('href');
      if (!href) return null;
      var url;
      try { url = new URL(href, root.location.href); } catch (error) { return null; }
      if (url.origin !== root.location.origin || url.pathname !== '/pages/compare-basses' || url.hash !== '#compare-' + intent) return null;
      return {
        intent: intent,
        href: href,
        productHandle: anchor.getAttribute('data-sa-comparison-product-handle') || '',
        trigger: anchor
      };
    }

    function onDocumentClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var anchor = event.target && event.target.closest ? event.target.closest(TRIGGER_SELECTOR) : null;
      if (!anchor || anchor.target || anchor.hasAttribute('download')) return;
      var request = canonicalTrigger(anchor);
      if (!request) return;
      var controller = soleLiveController();
      if (!controller) return;
      event.preventDefault();
      controller.open(request, true);
    }

    function requestFromHash() {
      var hash = String(root.location.hash || '').toLowerCase();
      var intent = HASH_INTENTS[hash];
      if (!intent) return;
      var controller = soleLiveController();
      if (!controller) return;
      var pageHandles = new Set();
      document.querySelectorAll(TRIGGER_SELECTOR + '[data-sa-comparison-product-handle]').forEach(function (anchor) {
        var request = canonicalTrigger(anchor);
        if (request && request.productHandle) pageHandles.add(request.productHandle);
      });
      controller.open({
        intent: intent,
        href: '/pages/compare-basses#compare-' + intent,
        productHandle: pageHandles.size === 1 ? Array.from(pageHandles)[0] : '',
        trigger: null
      }, false);
    }

    function destroyWithin(scope) {
      queryWithin(scope, HOST_SELECTOR).forEach(function (host) {
        var controller = controllers.get(host);
        if (!controller) return;
        controller.destroy();
        liveControllers.delete(controller);
        controllers.delete(host);
      });
    }

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('shopify:section:load', function (event) { mount(event.target); });
    document.addEventListener('shopify:section:unload', function (event) { destroyWithin(event.target); });
    root.addEventListener('hashchange', requestFromHash);
    root.addEventListener('pagehide', function (event) {
      Array.from(liveControllers).forEach(function (controller) {
        if (event.persisted) controller.close(false);
        else {
          controller.destroy();
          liveControllers.delete(controller);
        }
      });
    });
    root.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      mount(document);
      Array.from(liveControllers).forEach(function (controller) { controller.close(false); });
    });

    function boot() {
      mount(document);
      requestFromHash();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
    else boot();
  }

  function createController(root, host, loadCatalogue) {
    var document = root.document;
    var dialog = host.querySelector('[data-sa-comparison-dialog]');
    var body = host.querySelector('[data-sa-comparison-body]');
    var content = host.querySelector('[data-sa-comparison-content]');
    var specs = host.querySelector('[data-sa-comparison-specs]');
    var closeButton = host.querySelector('[data-sa-comparison-close]');
    var backdrop = host.querySelector('[data-sa-comparison-backdrop]');
    var catalogueUrl = host.getAttribute('data-sa-comparison-catalogue-url');
    if (!dialog || !body || !content || !specs || !closeButton || !backdrop || !catalogueUrl) {
      report(root, new Error('Required host roles are missing.'), 'mount');
      return null;
    }

    var destroyed = false;
    var open = false;
    var intentVersion = 0;
    var currentIntent = 'listen';
    var currentProductHandle = '';
    var currentFallback = '/pages/compare-basses#compare-listen';
    var openingTrigger = null;
    var catalogue = null;
    var playback = null;
    var disposePlayback = null;
    var progressFrame = 0;
    var scrollLocked = false;
    var listenRendered = false;
    var resizeObserver = null;
    var highlightedSpecColumn = null;

    function safe(operation, phase) {
      try { return operation(); } catch (error) { report(root, error, phase); }
    }

    function lockScroll() {
      if (scrollLocked) return;
      if (!root.SAScrollLock || typeof root.SAScrollLock.lock !== 'function') throw new Error('Scroll Lock is unavailable.');
      root.SAScrollLock.lock();
      scrollLocked = true;
    }

    function unlockScroll() {
      if (!scrollLocked) return;
      scrollLocked = false;
      if (root.SAScrollLock && typeof root.SAScrollLock.unlock === 'function') root.SAScrollLock.unlock();
    }

    function cancelProgress() {
      if (!progressFrame) return;
      root.cancelAnimationFrame(progressFrame);
      progressFrame = 0;
    }

    function pausePlayback() {
      cancelProgress();
      if (playback) safe(function () { playback.pause(); }, 'pause');
    }

    function focusable() {
      return Array.prototype.filter.call(dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'), function (element) {
        return !element.hidden && !element.closest('[hidden]') && !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true';
      });
    }

    function focusSoon(target, version) {
      root.setTimeout(function () {
        if (destroyed || !open || version !== intentVersion || !target || !target.isConnected) return;
        safe(function () { target.focus({ preventScroll: true }); }, 'focus');
      }, 0);
    }

    function showDialog(version) {
      var wasOpen = open;
      dialog.hidden = false;
      dialog.classList.add('is-open');
      dialog.setAttribute('aria-hidden', 'false');
      open = true;
      safe(lockScroll, 'scroll lock');
      if (!wasOpen) root.SAModalMotion.open(dialog);
      focusSoon(closeButton, version);
    }

    function setHash(intent) {
      if (!root.history || typeof root.history.replaceState !== 'function') return;
      safe(function () {
        root.history.replaceState(root.history.state, '', root.location.pathname + root.location.search + '#compare-' + intent);
      }, 'hash');
    }

    function clearHash() {
      if (!HASH_INTENTS[String(root.location.hash || '').toLowerCase()] || !root.history || typeof root.history.replaceState !== 'function') return;
      safe(function () {
        root.history.replaceState(root.history.state, '', root.location.pathname + root.location.search);
      }, 'hash cleanup');
    }

    function showStatus(title, message, retry) {
      specs.hidden = true;
      content.hidden = false;
      content.innerHTML = '<div class="sa-comparison__status" tabindex="-1">' +
        '<strong>' + escapeHtml(title) + '</strong>' +
        '<span>' + escapeHtml(message) + '</span>' +
        '<div class="sa-comparison__status-actions">' +
        (retry ? '<button type="button" data-sa-comparison-retry>Try again</button>' : '') +
        '<a href="' + escapeHtml(currentFallback) + '">Open the full comparison page</a>' +
        '</div></div>';
      focusSoon(content.querySelector('.sa-comparison__status'), intentVersion);
    }

    function playableProduct(config, handle) {
      var exact = config.products.find(function (product) { return product.productHandle === handle; });
      if (handle && !exact) report(root, new Error('Unknown Product handle: ' + handle), 'selection');
      var candidates = exact ? [exact].concat(config.products.filter(function (product) { return product !== exact; })) : config.products;
      var unavailable = new Set(config.unavailable.map(function (selection) {
        return selection.song + '|' + selection.mediaId + '|' + selection.mix;
      }));
      for (var productIndex = 0; productIndex < candidates.length; productIndex += 1) {
        for (var songIndex = 0; songIndex < config.songs.length; songIndex += 1) {
          for (var mixIndex = 0; mixIndex < config.mixes.length; mixIndex += 1) {
            if (!unavailable.has(config.songs[songIndex].id + '|' + candidates[productIndex].mediaId + '|' + config.mixes[mixIndex].id)) {
              return candidates[productIndex];
            }
          }
        }
      }
      throw new Error('No playable Product is available.');
    }

    function defaultMix(config) { return config.mixes[1] ? config.mixes[1].id : config.mixes[0].id; }

    function clearSpecColumnHighlight() {
      specs.querySelectorAll('.is-col-active').forEach(function (cell) { cell.classList.remove('is-col-active'); });
      highlightedSpecColumn = null;
    }

    function specColumnFromTarget(target) {
      var cell = target && target.closest ? target.closest('[data-col]') : null;
      if (!cell || !specs.contains(cell)) return null;
      var column = cell.getAttribute('data-col');
      return /^\d+$/.test(column) ? column : null;
    }

    function highlightSpecColumn(target) {
      var column = specColumnFromTarget(target);
      if (column === null) { clearSpecColumnHighlight(); return; }
      if (column === highlightedSpecColumn && specs.querySelector('.is-col-active')) return;
      clearSpecColumnHighlight();
      specs.querySelectorAll('[data-col="' + column + '"]').forEach(function (cell) { cell.classList.add('is-col-active'); });
      highlightedSpecColumn = column;
    }

    function onSpecsMouseover(event) { highlightSpecColumn(event.target); }
    function onSpecsMouseleave() { clearSpecColumnHighlight(); }
    function onSpecsFocusin(event) { highlightSpecColumn(event.target); }
    function onSpecsFocusout(event) {
      if (specColumnFromTarget(event.relatedTarget) !== null) highlightSpecColumn(event.relatedTarget);
      else clearSpecColumnHighlight();
    }

    function renderListen(config, selectedMediaId, selectedMix) {
      clearSpecColumnHighlight();
      var playIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>';
      var pauseIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>';
      var selected = config.products.find(function (product) { return product.mediaId === selectedMediaId; }) || config.products[0];
      var html = '<div class="sa-cmp__bar">' +
        '<div class="sa-cmp__now"><button class="sa-cmp__play" data-cmp-play type="button" aria-label="Play ' + escapeHtml(selected.label) + '">' +
        '<span class="sa-cmp__play-ic sa-cmp__play-ic--play">' + playIcon + '</span>' +
        '<span class="sa-cmp__play-ic sa-cmp__play-ic--pause">' + pauseIcon + '</span><span class="sa-cmp__play-spin"></span></button>' +
        '<span class="sa-cmp__now-label" data-cmp-now>' + escapeHtml(selected.label) + '</span></div>' +
        '<div class="sa-cmp__dropdown" role="group" aria-label="Demo genre">';
      config.songs.forEach(function (song, index) {
        html += '<button class="sa-cmp__dropdown-item' + (index === 0 ? ' is-active' : '') + '" data-cmp-song="' + escapeHtml(song.id) + '" type="button" aria-pressed="' + (index === 0) + '">' + escapeHtml(song.label) + '</button>';
      });
      html += '</div><div class="sa-cmp__pills" data-cmp-mixes>';
      config.mixes.forEach(function (mix) {
        html += '<button class="sa-cmp__pill' + (mix.id === selectedMix ? ' is-active' : '') + '" data-cmp-mix="' + escapeHtml(mix.id) + '" type="button" aria-pressed="' + (mix.id === selectedMix) + '" aria-label="' + escapeHtml(mix.label) + ' mix"><span class="sa-cmp__pill-label">' + escapeHtml(mix.label) + '</span><span class="sa-cmp__pill-spin"></span></button>';
      });
      html += '</div></div><div class="sa-cmp__scroll-region"><div class="sa-cmp__scroll-wrap">';

      function tiles(products) {
        var output = '<div class="sa-cmp__grid">';
        products.forEach(function (product) {
          var active = product.mediaId === selectedMediaId;
          var meta = product.summary.playstyle + ' · ' + product.summary.context;
          var label = 'Play ' + product.label + ', ' + meta;
          output += '<div class="sa-cmp__tile-shell"><button class="sa-cmp__tile' + (active ? ' is-active' : '') + '" data-cmp-media-id="' + escapeHtml(product.mediaId) + '" data-cmp-tile-label="' + escapeHtml(label) + '" type="button" aria-pressed="' + active + '" aria-label="' + escapeHtml(label) + '">' +
            '<img class="sa-cmp__tile-img" src="' + escapeHtml(product.image) + '" alt="' + escapeHtml(product.label) + '" loading="lazy">' +
            '<span class="sa-cmp__tile-name">' + escapeHtml(product.label) + '</span><span class="sa-cmp__tile-meta">' + escapeHtml(meta) + '</span>' +
            '<span class="sa-cmp__tile-spin"></span><div class="sa-cmp__tile-progress"></div><span class="sa-cmp__tile-ic">' +
            playIcon.replace('<svg', '<svg class="sa-cmp__tile-ic--play"') +
            pauseIcon.replace('<svg', '<svg class="sa-cmp__tile-ic--pause"') + '</span></button>' +
            '<a class="sa-cmp__tile-learn" href="/products/' + escapeHtml(product.productHandle) + '" aria-label="Learn more about ' + escapeHtml(product.label) + '" tabindex="' + (active ? '0' : '-1') + '" aria-hidden="' + (!active) + '"><span class="sa-cmp__tile-learn-text">Learn more</span><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M7 17L17 7M7 7h10v10"/></svg></a></div>';
        });
        return output + '</div>';
      }

      html += tiles(config.products.filter(function (product) { return !product.legacy; }));
      var legacy = config.products.filter(function (product) { return product.legacy; });
      if (legacy.length) html += '<div class="sa-cmp__eyebrow">Legacy</div>' + tiles(legacy);
      html += '</div><div class="sa-cmp__scrollbar" aria-hidden="true"><div class="sa-cmp__scrollbar-thumb"></div></div></div>';
      content.innerHTML = html;
      content.hidden = false;
      specs.hidden = true;
      listenRendered = true;
      installScrollbar();
    }

    function renderSpecs(config) {
      clearSpecColumnHighlight();
      var tick = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" aria-label="Included"><path d="M2.5 7 5.5 10 11.5 4"/></svg>';
      var cross = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" aria-label="Not included"><path d="m3.5 3.5 7 7m0-7-7 7"/></svg>';
      var notApplicable = '<span class="sa-cmp-specs__na" aria-label="Not applicable">—</span>';
      var html = '<div class="sa-cmp-specs__head"><span class="sa-cmp-specs__title">Specifications</span></div><div class="sa-cmp-specs__scroll" tabindex="-1"><table class="sa-cmp-specs__table"><thead><tr><th class="sa-cmp-specs__corner" data-col="-1"></th>';
      config.products.forEach(function (product, index) { html += '<th class="sa-cmp-specs__bass-name" data-col="' + index + '" tabindex="0">' + escapeHtml(product.label) + '</th>'; });
      html += '</tr></thead><tbody>';

      function row(label, values) {
        html += '<tr><td class="sa-cmp-specs__label" data-col="-1">' + escapeHtml(label) + '</td>';
        values.forEach(function (value, index) { html += '<td class="sa-cmp-specs__val" data-col="' + index + '">' + value + '</td>'; });
        html += '</tr>';
      }

      row('Tones', config.products.map(function (product) { return String(product.specs.tones); }));
      row('Strings', config.products.map(function (product) { return String(product.specs.strings); }));
      row('Highest Note', config.products.map(function (product) { return escapeHtml(product.specs.highestNote || '—'); }));
      row('Lowest Note', config.products.map(function (product) { return escapeHtml(product.specs.lowestNote || '—'); }));
      row('Articulations', config.products.map(function (product) { return String(product.specs.articulations.length); }));
      row('Playstyle', config.products.map(function (product) { return escapeHtml(product.summary.playstyle); }));

      config.specificationEngines.forEach(function (engine) {
        html += '<tr class="sa-cmp-specs__divider"><td class="sa-cmp-specs__divider-label" data-col="-1">' + escapeHtml(engine.label) + '</td>';
        config.products.forEach(function () { html += '<td></td>'; });
        html += '</tr>';
        engine.articulations.forEach(function (articulation) {
          row(articulation.label, config.products.map(function (product) {
            if (product.specs.engine !== engine.id) return notApplicable;
            return product.specs.articulations.indexOf(articulation.id) >= 0 ? tick : cross;
          }));
        });
      });
      html += '</tbody></table></div>';
      specs.innerHTML = html;
      specs.hidden = false;
      content.hidden = true;
    }

    function installScrollbar() {
      if (resizeObserver) { resizeObserver.disconnect(); resizeObserver = null; }
      var region = content.querySelector('.sa-cmp__scroll-region');
      var scroll = content.querySelector('.sa-cmp__scroll-wrap');
      var thumb = content.querySelector('.sa-cmp__scrollbar-thumb');
      if (!region || !scroll || !thumb) return;
      function sync() {
        if (destroyed || !listenRendered) return;
        var overflow = scroll.scrollHeight > scroll.clientHeight + 2;
        region.classList.toggle('has-overflow', overflow);
        if (!overflow) return;
        var trackHeight = thumb.parentElement.clientHeight;
        var thumbHeight = Math.max(28, trackHeight * scroll.clientHeight / scroll.scrollHeight);
        var range = Math.max(1, scroll.scrollHeight - scroll.clientHeight);
        thumb.style.height = thumbHeight + 'px';
        thumb.style.top = (scroll.scrollTop / range * Math.max(0, trackHeight - thumbHeight)) + 'px';
      }
      scroll.addEventListener('scroll', sync, { passive: true });
      if (typeof root.ResizeObserver === 'function') {
        resizeObserver = new root.ResizeObserver(sync);
        resizeObserver.observe(scroll);
      }
      root.requestAnimationFrame(sync);
    }

    function ensurePlayback(config, selectedProduct) {
      if (playback) return playback;
      playback = createPlaybackAdapter(root, config, 'sa-product-comparison-' + Math.random().toString(36).slice(2));
      disposePlayback = playback.subscribe(function (state) {
        if (!destroyed && open && currentIntent === 'listen') updateListen(state);
      });
      playback.init(config.songs[0].id, defaultMix(config), selectedProduct.mediaId);
      return playback;
    }

    function updateListen(state) {
      if (!listenRendered) return;
      var selectedProduct = catalogue.products.find(function (product) { return product.mediaId === state.mediaId; });
      var playButton = content.querySelector('[data-cmp-play]');
      if (playButton) {
        playButton.classList.toggle('is-playing', state.playing);
        playButton.classList.toggle('is-loading', state.status === 'loading' || state.status === 'preparing');
        playButton.setAttribute('aria-label', (state.playing ? 'Pause ' : 'Play ') + (selectedProduct ? selectedProduct.label : 'bass'));
      }
      var now = content.querySelector('[data-cmp-now]');
      if (now && selectedProduct) now.textContent = selectedProduct.label;
      content.querySelectorAll('[data-cmp-song]').forEach(function (button) {
        var active = button.getAttribute('data-cmp-song') === state.song;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      content.querySelectorAll('[data-cmp-mix]').forEach(function (button) {
        var active = button.getAttribute('data-cmp-mix') === state.mix;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      content.querySelectorAll('[data-cmp-media-id]').forEach(function (tile) {
        var mediaId = tile.getAttribute('data-cmp-media-id');
        var active = mediaId === state.mediaId;
        var load = state.loadState[state.song + '-' + mediaId + '-' + state.mix];
        tile.classList.toggle('is-active', active);
        tile.classList.toggle('is-playing', active && state.playing);
        tile.classList.toggle('is-loading', load === 'loading' && !active);
        tile.classList.toggle('is-error', load === 'error');
        tile.disabled = load === 'error';
        tile.setAttribute('aria-pressed', String(active));
        var learn = tile.parentElement.querySelector('.sa-cmp__tile-learn');
        if (learn) {
          learn.tabIndex = active && load !== 'error' ? 0 : -1;
          learn.setAttribute('aria-hidden', String(!active || load === 'error'));
        }
      });
      cancelProgress();
      updateProgress(state);
      if (state.playing) {
        var tick = function () {
          if (!playback || destroyed || !open || currentIntent !== 'listen') { progressFrame = 0; return; }
          updateProgress(playback.getState());
          progressFrame = root.requestAnimationFrame(tick);
        };
        progressFrame = root.requestAnimationFrame(tick);
      }
    }

    function updateProgress(state) {
      var ratio = state.duration > 0 ? Math.min(1, state.position / state.duration) : 0;
      content.querySelectorAll('.sa-cmp__tile-progress').forEach(function (bar) {
        var tile = bar.closest('[data-cmp-media-id]');
        bar.style.transform = tile && tile.getAttribute('data-cmp-media-id') === state.mediaId && state.playing ? 'scaleX(' + ratio.toFixed(4) + ')' : 'scaleX(0)';
      });
    }

    function applyIntent(config, version) {
      if (destroyed || !open || version !== intentVersion) return;
      var selectedProduct;
      try { selectedProduct = playableProduct(config, currentProductHandle); }
      catch (error) { showStatus('Comparison unavailable', error.message, false); return; }

      host.querySelectorAll('[data-sa-comparison-view]').forEach(function (button) {
        var active = button.getAttribute('data-sa-comparison-view') === currentIntent;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        button.removeAttribute('aria-selected');
        button.removeAttribute('tabindex');
      });

      if (currentIntent === 'specs') {
        pausePlayback();
        renderSpecs(config);
        focusSoon(specs.querySelector('.sa-cmp-specs__scroll') || closeButton, version);
        return;
      }

      var selectedMix = playback ? playback.getState().mix : defaultMix(config);
      renderListen(config, selectedProduct.mediaId, selectedMix);
      try {
        var adapter = ensurePlayback(config, selectedProduct);
        if (adapter.getState().mediaId !== selectedProduct.mediaId) adapter.setMediaId(selectedProduct.mediaId);
        updateListen(adapter.getState());
        focusSoon(content.querySelector('[data-cmp-play]') || closeButton, version);
      } catch (error) {
        report(root, error, 'playback');
        showStatus('Audio unavailable', 'Specifications remain available, or open the full comparison page.', false);
      }
    }

    function submit(request, updateHash) {
      if (destroyed) return;
      intentVersion += 1;
      var version = intentVersion;
      currentIntent = request.intent;
      currentProductHandle = request.productHandle || '';
      currentFallback = request.href || '/pages/compare-basses#compare-' + currentIntent;
      openingTrigger = request.trigger || openingTrigger;
      showDialog(version);
      if (updateHash) setHash(currentIntent);
      if (!catalogue) showStatus('Loading comparison…', 'Preparing the Product Comparison.', false);
      loadCatalogue(catalogueUrl, false).then(function (loaded) {
        if (destroyed || !open || version !== intentVersion) return;
        catalogue = loaded;
        applyIntent(loaded, version);
      }).catch(function (error) {
        if (destroyed || !open || version !== intentVersion) return;
        report(root, error, 'catalogue');
        showStatus('Comparison unavailable', 'The comparison catalogue could not be loaded.', true);
      });
    }

    function close(restoreFocus) {
      intentVersion += 1;
      pausePlayback();
      open = false;
      dialog.setAttribute('aria-hidden', 'true');
      safe(unlockScroll, 'scroll unlock');
      clearHash();
      if (restoreFocus !== false && openingTrigger && openingTrigger.isConnected && typeof openingTrigger.focus === 'function') {
        safe(function () { openingTrigger.focus({ preventScroll: true }); }, 'focus restore');
      }
      openingTrigger = null;
      root.SAModalMotion.close(dialog, function () {
        dialog.classList.remove('is-open');
        dialog.hidden = true;
      });
    }

    function onClick(event) {
      var target = event.target;
      if (target.closest('[data-sa-comparison-close]') || target.closest('[data-sa-comparison-backdrop]')) { close(true); return; }
      var view = target.closest('[data-sa-comparison-view]');
      if (view) {
        submit({ intent: view.getAttribute('data-sa-comparison-view'), href: currentFallback, productHandle: currentProductHandle, trigger: openingTrigger }, true);
        return;
      }
      var retry = target.closest('[data-sa-comparison-retry]');
      if (retry) {
        intentVersion += 1;
        var version = intentVersion;
        showStatus('Loading comparison…', 'Preparing the Product Comparison.', false);
        loadCatalogue(catalogueUrl, true).then(function (loaded) {
          if (destroyed || !open || version !== intentVersion) return;
          catalogue = loaded;
          applyIntent(loaded, version);
        }).catch(function (error) {
          if (destroyed || !open || version !== intentVersion) return;
          report(root, error, 'catalogue retry');
          showStatus('Comparison unavailable', 'The comparison catalogue could not be loaded.', true);
        });
        return;
      }
      if (!playback || currentIntent !== 'listen') return;
      if (target.closest('.sa-cmp__tile-learn')) return;
      var song = target.closest('[data-cmp-song]');
      var mix = target.closest('[data-cmp-mix]');
      var play = target.closest('[data-cmp-play]');
      var tile = target.closest('[data-cmp-media-id]');
      try {
        if (song) playback.setSong(song.getAttribute('data-cmp-song'));
        else if (mix) playback.setMix(mix.getAttribute('data-cmp-mix'));
        else if (play) playback.toggle();
        else if (tile && !tile.disabled) {
          var next = tile.getAttribute('data-cmp-media-id');
          var state = playback.getState();
          if (next === state.mediaId) playback.toggle();
          else {
            playback.setMediaId(next);
            if (!state.playing) playback.toggle();
          }
        }
      } catch (error) {
        report(root, error, 'interaction');
        pausePlayback();
        showStatus('Audio unavailable', 'No Product is available for this song and mix.', false);
      }
    }

    function onKeydown(event) {
      if (!open) return;
      if (event.key === 'Escape') { event.preventDefault(); close(true); return; }
      if (event.key !== 'Tab') return;
      var nodes = focusable();
      if (!nodes.length) { event.preventDefault(); body.focus({ preventScroll: true }); return; }
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus({ preventScroll: true }); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus({ preventScroll: true }); }
    }

    host.addEventListener('click', onClick);
    specs.addEventListener('mouseover', onSpecsMouseover);
    specs.addEventListener('mouseleave', onSpecsMouseleave);
    specs.addEventListener('focusin', onSpecsFocusin);
    specs.addEventListener('focusout', onSpecsFocusout);
    document.addEventListener('keydown', onKeydown);

    return Object.freeze({
      get live() { return !destroyed && host.isConnected; },
      open: submit,
      close: close,
      destroy: function () {
        if (destroyed) return;
        close(false);
        destroyed = true;
        intentVersion += 1;
        host.removeEventListener('click', onClick);
        specs.removeEventListener('mouseover', onSpecsMouseover);
        specs.removeEventListener('mouseleave', onSpecsMouseleave);
        specs.removeEventListener('focusin', onSpecsFocusin);
        specs.removeEventListener('focusout', onSpecsFocusout);
        clearSpecColumnHighlight();
        document.removeEventListener('keydown', onKeydown);
        if (resizeObserver) resizeObserver.disconnect();
        resizeObserver = null;
        if (disposePlayback) disposePlayback();
        disposePlayback = null;
        if (playback) safe(function () { playback.destroy(); }, 'playback destroy');
        playback = null;
      }
    });
  }

  return Object.freeze({
    validateCatalogue: validateCatalogue,
    buildMediaUrl: buildMediaUrl,
    install: install
  });
}));
